import { useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  creditNoteAppliedAmountOf,
  creditNoteOffsetAmountOf,
  effectivePaymentSourceRankOf,
  effectiveSettlementRatioSummary,
  outstandingOf,
  overdueDaysOf,
  overdueSinceOf,
  paidAmountOf,
  paidPctOf,
  prepaymentCoveredByFinalAmountOf,
  prepaymentReviewNeeded,
  PRIORITIES,
  resolveEntry,
  settlementStatusOf,
  signedEffectiveSumEntries,
  signedOutstandingOf,
  signedOutstandingSum,
  signedSumEntries,
  splitMoney,
  totalDocumentOffsetOf,
  type Basis,
  type CashFlowEntry as CoreEntry,
  type SettlementStatus,
} from '@progear/finance-core';
import { cn } from '@/lib/utils';
import { payrollNetEntryMonth } from '@/lib/loans';
import { DateText } from '@/components/ui/DateText';
import { EtDateInput } from '@/components/ui/EtDateInput';
import {
  FORECAST_PILL_CLS,
  FORECAST_PILL_LABEL,
  FORECAST_PILL_TITLE,
  forecastAmountClass,
  forecastBaseClass,
  forecastEmphasisClass,
  formatEtDate,
  formatEur,
  formatSettledPct,
  parseEurCents,
  pickMoney,
  pickSum,
  PRIORITY_CLS,
  PRIORITY_LABEL,
  SETTLEMENT_STATUS_LABEL,
  type CashFlowEntry,
  type CreateEntrySeed,
  type MarkPaidIntent,
  type MoneyMode,
  type ResolveSettlementIntent,
} from '@/lib/cashFlow';
import { summarizeLedgerScope } from '@/lib/ledgerScopeHeader';
import { docCell, docTypeBadgeCls, liikCell, popoverOpensUp, priorityCell, projectCompact } from '@/lib/ledgerCells';
import { relationCaption, relationLinkLabel } from '@/lib/relatedDocuments';
import { LedgerStatusCell } from './LedgerStatusCell';
import { MarkPaidPanel } from './settlement/MarkPaidPanel';
import { SettlementSourceCell } from './SettlementSourceCell';
import { DirectOpenButton } from './DirectOpenButton';
import { DocHoverCard, GroupHoverCard } from './calendar/HoverCards';
import type { HoverPos } from './calendar/model';
import { useHoverIntent } from '@/hooks/useHoverIntent';
import { PlainAmount, SignedEur } from './SignedEur';
import { LINE_META } from './categoryMeta';
import { PeriodPicker } from './PeriodPicker';
import {
  buildFacets,
  emptyFilters,
  entryMatchesFilters,
  facetCounts,
  filterCount,
  TXN_TYPE_LABEL,
  type FilterKey,
} from '@/lib/cashFlowFilters';
import { FilterPanel, FilterChipBar } from './CashFlowFilter';
import { useCashFlowWorkspace } from './CashFlowWorkspace';
import { useLocateScroll } from '@/hooks/useLocateScroll';
import {
  groupLedgerRows,
  LEDGER_GROUP_OPTIONS,
  type LedgerGroup,
  type LedgerGroupKey,
} from '@/lib/ledgerGrouping';
import { buildLedgerCsv, downloadLedgerCsv } from '@/lib/ledgerCsv';

/**
 * "Kõik dokumendid" — the universal ledger of the primary view, and the canonical
 * app-wide document tool. A dense, fixed-layout, sortable, filterable table of every
 * cash-flow document, reachable from the toolbar, the overdue indicator, or a drill
 * (via `scope`). It reads the SAME finance-core entries as the matrix/calendar — each
 * row's direction, line and effective date are resolved by `resolveEntry`, its
 * settlement status by `settlementStatusOf`; every total (footer AND the selection
 * tally) is the SIGNED cash-flow sum from `signedSumEntries` / `sumSettlement` (L9 /
 * master rule — the component never adds money itself). Money-mode, the faceted filter,
 * the period window and the cross-lens selection all live in the workspace, so toggling
 * any of them here moves EVERY lens identically (replit.md L14: the lenses share every
 * function + state).
 */

/** A drill context the ledger can be opened with (a cell, a line, a day, overdue). */
export interface LedgerScope {
  label: string;
  match: (row: LedgerRow) => boolean;
}

export interface LedgerProps {
  entries: CashFlowEntry[];
  today: string;
  scope?: LedgerScope | null;
  onClose: () => void;
  /** Carry an unpaid document's due date to today (edasi kantud). Persisted by the caller. */
  onReschedule?: (id: string, expectedDate: string) => void;
  /** Record a manual settlement (Manuaal source event) for a document — the shared
   *  mark-paid intent (date + optional PARTIAL amount) collected by the ONE
   *  MarkPaidPanel both lenses mount (L14; the detail's Arveldus is the other mount).
   *  The caller mints the idempotency key and books the amount server-side (L9). */
  onMarkPaid?: (id: string, intent: MarkPaidIntent) => void;
  /** Carve a GROSS amount off a document onto a target date (poolita) — the SAME
   *  split the calendar drag exposes, so the function is reachable from BOTH lenses
   *  (L14). Persisted by the caller (sim-aware). */
  onSplit?: (id: string, targetDate: string, moveGrossCents: number) => void;
  /** Save the free-text "Kommentaar" (PATCH); `null` clears it. Persisted by the caller. */
  onComment?: (id: string, comment: string | null) => void;
  /** Set/clear the importance flag (KRIITILINE/OLULINE/FOOKUSES) from the register's
   *  "Halda" popover (PATCH); `null` clears it. Persisted by the caller (F2). */
  onPriority?: (id: string, priority: CashFlowEntry['priority']) => void;
  /** Resolve an outstanding gap (kanna maha / faktooring) from the register's "Halda"
   *  popover. This is the register-only lens of the settlement-resolution write — the
   *  matrix/calendar reach it via drill-to-register (L14). The caller mints the
   *  idempotency key and computes every amount server-side (the intent carries no money
   *  beyond the optional factoring fee). Persisted by the caller. */
  onResolve?: (id: string, intent: ResolveSettlementIntent) => void;
  /** Open the shared enriched-entry / document detail for ONE entry — the SAME drill
   *  surface the calendar lens opens (L14: reachable from BOTH lenses, never table-only).
   *  Powers the per-row direct-open document icon. */
  onOpenEntry?: (id: string) => void;
  /** Open the ONE shared create dialog from the register toolbar (seeded with today) —
   *  the register's lens-appropriate "+", the SAME create surface the calendar/table
   *  expose, so create is reachable from every lens (L14). */
  onAddEntry?: (seed?: CreateEntrySeed) => void;
  /** Hard-delete a manual entry from its "Halda" popover (the document-level destructive
   *  action). Offered ONLY for `source === 'manual'`; the server is the authoritative
   *  guard (409 otherwise). Reachable from every lens via drill-to-register (L14). */
  onDelete?: (id: string) => void;
}

export interface LedgerRow {
  e: CashFlowEntry;
  suund: 'income' | 'cost';
  /** Document number (`docNumber`), falling back to description then id. */
  dok: string;
  /** Document type (`docType`) raw code — labelled via DOC_TYPE_LABEL, or null. */
  docType: CashFlowEntry['docType'];
  /** Offer / quote number (`offerNumber`), or null — kept for free-text search. */
  offer: string | null;
  osapool: string;
  liik: string;
  category: CashFlowEntry['category'];
  /** Tehingu liik (`txnType`) raw value, or null — shown as a badge. */
  txnType: CashFlowEntry['txnType'];
  /** Project name (`projectName`), falling back to the raw `project` tag, or null. */
  project: string | null;
  tahtaeg: string;
  /** True when this row is an overdue, still-unpaid document rolled forward to today
   *  (edasi kantud) — `resolveEntry(e, today).carriedForward`; drives the Tähtaeg flag. */
  carriedForward: boolean;
  /** Overdue age of a carried-forward row, or null when not carried: whole days past
   *  the ORIGINAL due day (`overdueDaysOf`) + that origin day (`overdueSinceOf` —
   *  a reschedule/split keeps `originExpectedDate`, so the age never resets when a
   *  document is pushed forward). Drives the Tähtaeg origin date + the Staatus
   *  "N p" badge. Both figures are finance-core reads (no component date maths). */
  overdueAge: { days: number; since: string } | null;
  /** Resolved certainty (`resolveEntry(e, today).effectiveCertainty`) — drives the shared
   *  forecast-emphasis wash so the register lifts forecast rows in step with the calendar. */
  certainty: 'actual' | 'committed' | 'forecast';
  /** Document (issue) date (`documentDate`) — statutory / loan rows carry null. */
  docDate: string | null;
  /** Paid amount in cents — finance-core `paidAmountOf` (legacy mark-paid OR events);
   *  feeds the CSV "Tasutud" column (the on-screen figure lives in the rich hover). */
  tasutud: number;
  /** Paid share as a whole percent — finance-core `paidPctOf` (cash paid / gross);
   *  the status badge shows it ONLY when partly settled. */
  tasutudPct: number;
  /** Signed outstanding in cents — finance-core `signedOutstandingOf` (income +,
   *  cost −), so the "Jääk" column reads directionally just like "Summa". */
  jaak: number;
  status: SettlementStatus;
  /** Document author / creator (`author`), or null. */
  looja: string | null;
  /** Free-text user note (`comment`), or null — editable in place. */
  comment: string | null;
  /** True on a stale katteta ettemaks — an income ETT past its due day with no cash
   *  and no final invoice / kreeditarve linked (finance-core `prepaymentReviewNeeded`);
   *  drives the shared amber "kontrolli" marker in the Staatus cell. */
  reviewNeeded: boolean;
}

type SortKey =
  | 'suund'
  | 'dok'
  | 'osapool'
  | 'liik'
  | 'project'
  | 'tahtaeg'
  | 'docdate'
  | 'summa'
  | 'net'
  | 'vat'
  | 'gross'
  | 'jaak'
  | 'source'
  | 'staatus';

/** The quick status chips — 'all' plus the canonical settlement taxonomy (L14). */
const STATUS_FILTERS: ('all' | SettlementStatus)[] = [
  'all',
  'settled',
  'partial',
  'overdue',
  'pending',
  'planned',
];
const statusFilterLabel = (s: 'all' | SettlementStatus): string =>
  s === 'all' ? 'Kõik' : SETTLEMENT_STATUS_LABEL[s];

interface LedgerColumn {
  key: SortKey;
  label: string;
  align: 'left' | 'right';
  width: number;
  /** Small bold marker after the label (like the Summa column's N/B mode letter) —
   *  the Jääk column carries a fixed "B": outstanding money is ALWAYS bruto. */
  marker?: string;
  /** Header tooltip explaining WHAT the column shows (the sort hint is implied). */
  headTitle?: string;
}

/** The single mode-aware "Summa" column width — fixed so flipping the money mode
 *  (Neto / Bruto) NEVER shifts the sticky tail / Halda columns (req 5). */
const SUMMA_W = 120;
/** Each sub-column width when the money block is EXPANDED to Neto · KM · Bruto — a
 *  presentation-only breakdown of the SAME `Money`, never a money-mode (v1.4 / L9). */
const SUMMA_SUB_W = 96;

/** Fixed column geometry — `table-fixed` + the colgroup keep every row one line
 *  (nowrap + truncation), and give the sticky select / Halda columns a stable width.
 *  The text columns BEFORE the money block (lead) and the settlement columns AFTER it
 *  (tail) are static; only the money block in the middle expands/collapses. */
const LEAD_COLUMNS: LedgerColumn[] = [
  { key: 'suund', label: 'Suund', align: 'left', width: 34 },
  { key: 'dok', label: 'Dok', align: 'left', width: 132 },
  { key: 'osapool', label: 'Osapool', align: 'left', width: 132 },
  { key: 'liik', label: 'Liik', align: 'left', width: 60 },
  { key: 'project', label: 'Projekt', align: 'left', width: 92 },
  { key: 'tahtaeg', label: 'Tähtaeg', align: 'left', width: 124 },
  { key: 'docdate', label: 'Dok. kuup.', align: 'left', width: 100 },
];
const TAIL_COLUMNS: LedgerColumn[] = [
  {
    key: 'jaak',
    label: 'Jääk',
    align: 'right',
    width: 92,
    marker: 'B',
    headTitle:
      'Jääk — füüsiliselt veel laekumata / tasumata raha, ALATI BRUTO (koos käibemaksuga). Neto/Bruto lüliti seda veergu ei muuda. Tulu + (laekumata), kulu − (tasumata); tasutud dokument näitab —',
  },
  { key: 'source', label: 'Allikas', align: 'right', width: 88 },
  { key: 'staatus', label: 'Staatus', align: 'left', width: 146 },
];
const SELECT_W = 38;
const ACTION_W = 96;

/** The settlement-resolution kinds offered in the register's "Lahenda" panel — the
 *  union of `ResolveSettlementIntent['type']`. Write-offs (ümardus / lootusetu) and a
 *  retention withholding (tagatis) close the gap silently; factoring also books the
 *  fee as a real `financial_cost` line. */
const RESOLVE_TYPES = [
  'rounding_writeoff',
  'bad_debt_writeoff',
  'retention_deferred',
  'factoring_fee',
] as const;
type ResolveType = (typeof RESOLVE_TYPES)[number];
const RESOLVE_TYPE_LABEL: Record<ResolveType, string> = {
  rounding_writeoff: 'Ümardus',
  bad_debt_writeoff: 'Lootusetu',
  retention_deferred: 'Tagatis',
  factoring_fee: 'Faktooring',
};

/** Did a row-click originate from an interactive child (a control or the Halda menu)
 *  rather than the row surface itself? Those clicks drive their own action — select,
 *  Halda, direct-open, a form field — and must NOT also open the row's document detail.
 *  (The whole register lives inside a role="dialog", so that role is deliberately NOT
 *  in the selector — it would match the outer overlay ancestor and swallow every click.) */
function isInteractiveTarget(target: Element): boolean {
  return target.closest('button, input, a, label, select, textarea') !== null;
}

export function toRow(e: CashFlowEntry, today: string, basis: Basis = 'cashBasis'): LedgerRow {
  const r = resolveEntry(e as CoreEntry, today, basis);
  // Settlement is finance-core truth: status from `settlementStatusOf`, paid /
  // outstanding from `paidAmountOf` / `signedOutstandingOf` (the view's own
  // outstanding_cents is actualDate-blind, so it is never read here; L9).
  return {
    e,
    suund: e.kind,
    dok: e.docNumber ?? e.description ?? e.id,
    docType: e.docType,
    offer: e.offerNumber,
    osapool: e.party,
    liik: LINE_META[e.category].label,
    category: e.category,
    txnType: e.txnType,
    project: e.projectName ?? e.project,
    tahtaeg: r.effectiveDate,
    carriedForward: r.carriedForward,
    overdueAge: r.carriedForward
      ? { days: overdueDaysOf(e, today), since: overdueSinceOf(e) }
      : null,
    certainty: r.effectiveCertainty,
    docDate: e.documentDate,
    tasutud: paidAmountOf(e),
    tasutudPct: paidPctOf(e),
    jaak: signedOutstandingOf(e),
    status: settlementStatusOf(e, today),
    looja: e.author,
    comment: e.comment,
    reviewNeeded: prepaymentReviewNeeded(e as unknown as Parameters<typeof prepaymentReviewNeeded>[0], today),
  };
}

function sortVal(row: LedgerRow, key: SortKey, mode: MoneyMode): number | string {
  switch (key) {
    case 'summa':
      return pickMoney(row.e.money, mode);
    case 'net':
      return row.e.money.net;
    case 'vat':
      return row.e.money.vat;
    case 'gross':
      return row.e.money.gross;
    case 'docdate':
      return row.docDate ?? '';
    case 'jaak':
      return row.jaak;
    case 'source':
      // The settlement TRUTH source rank (lhv > smartaccounts > erply > manual; no cash
      // → MAX_SAFE_INTEGER, sorting unsettled rows last) straight from finance-core — the
      // register never re-derives the priority (master rule; L9).
      return effectivePaymentSourceRankOf(row.e);
    case 'suund':
      return row.suund;
    case 'dok':
      return row.dok;
    case 'osapool':
      return row.osapool;
    case 'project':
      return row.project ?? '';
    case 'liik':
      return row.liik;
    case 'tahtaeg':
      // Every carried-forward row's effective date is "today" (all equal — useless to
      // sort on), so they sort by their ORIGINAL due day instead: ascending Tähtaeg
      // reads "the longest-overdue document first".
      return row.overdueAge !== null ? row.overdueAge.since : row.tahtaeg;
    case 'staatus':
      return row.status;
  }
}

export function Ledger({
  entries,
  today,
  scope = null,
  onClose,
  onReschedule,
  onMarkPaid,
  onSplit,
  onComment,
  onPriority,
  onResolve,
  onOpenEntry,
  onAddEntry,
  onDelete,
}: LedgerProps) {
  // Money-mode, the faceted filter and the cross-lens selection are the SHARED workspace
  // state — toggling any of them here narrows / re-displays EVERY lens (calendar, table,
  // register), because all three read this one source. The period window is driven by the
  // shared `PeriodPicker` this lens renders, so it too re-scopes every lens (L14).
  const {
    mode,
    filters,
    setFilters,
    selectedIdSet,
    setSelected,
    toggleSelected,
    clearSelection,
    selectedOnly,
    setSelectedOnly,
    searchBehavior,
    setSearchBehavior,
    activeMatch,
    locateEntry,
    forecastEmphasis,
    basis,
  } = useCashFlowWorkspace();
  const [sortKey, setSortKey] = useState<SortKey>('tahtaeg');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  // PRESENTATION-only: collapse the money block to ONE workspace-mode "Summa" column, or
  // expand it to the Neto · KM · Bruto breakdown of the SAME `Money`. This is NOT a money
  // mode (the NET/GROSS mode is the shared workspace state) and never changes a figure —
  // only how many columns the same numbers are shown across (v1.4 / L9).
  const [summaExpanded, setSummaExpanded] = useState(false);
  // PRESENTATION-only: group the visible rows by ONE register facet (direction / line /
  // project), each group getting a header + a subtotal row (E1). Like sort + the Summa
  // expand, this is a register-lens affordance (the matrix groups by the 7 lines, the
  // calendar by day — L14); the subtotal NUMBERS are still the shared finance-core sums.
  const [groupBy, setGroupBy] = useState<LedgerGroupKey>('none');
  // R7 / L14 — the register's quick controls (search · Suund · Staatus) drive the
  // SHARED workspace filter state, never a register-local state, so the SAME interaction
  // narrows the calendar and matrix too. They are TOGGLE shortcuts over the very facets
  // the "Rohkem filtreid" panel exposes (search · direction · settlementStatus) — so the
  // register has ONE unified filter, not a second parallel one, and multiple values can
  // be combined (e.g. Üle tähtaja + Ootel), exactly like the panel's multi-select over
  // the same facet. A chip is "pressed" when its value is in the shared set; "Kõik"
  // clears the facet (and reads pressed while the facet is empty).
  const q = filters.search;
  const setSearch = (value: string) => setFilters((f) => ({ ...f, search: value }));
  // "Leia" (locate) mode keeps its own draft term — it must NOT touch the shared
  // `filters.search` (which narrows every lens); on Enter it reveals the first matching
  // row in place instead (see runLocate). 'filter' mode drives `filters.search` as before.
  const [locateQuery, setLocateQuery] = useState('');
  const dirSel = filters.sel.direction;
  const statSel = filters.sel.settlementStatus;
  const toggleQuickFacet = (key: 'direction' | 'settlementStatus', value: string | null) =>
    setFilters((f) => {
      const next = new Set(f.sel[key]);
      if (value === null) next.clear();
      else if (next.has(value)) next.delete(value);
      else next.add(value);
      return { ...f, sel: { ...f.sel, [key]: next } };
    });
  // Per-row chosen action date (ISO) — defaults to today; the reschedule / mark-paid
  // actions apply this date so the user isn't limited to "today" (et date input).
  const [actionDate, setActionDate] = useState<Record<string, string>>({});
  const dateFor = (id: string) => actionDate[id] ?? today;
  // Inline "poolita" editor: which row is open + the euro amount being carved off.
  const [splitId, setSplitId] = useState<string | null>(null);
  const [splitAmt, setSplitAmt] = useState('');
  // Inline mark-paid editor: which row's "Makstuks" panel is open (the register's
  // mount of the ONE shared MarkPaidPanel — the detail mounts the same panel, L14).
  const [markPaidId, setMarkPaidId] = useState<string | null>(null);
  // Inline "lahenda" editor: which row is open, the chosen resolution kind, the required
  // reason, and (factoring only) the fee € + factor party. The effective date reuses the
  // per-row `dateFor` picker. The amount closed is server-derived (L9) — we send only the
  // intent (kind + reason + optional factoring fee), never the outstanding itself.
  const [resolveId, setResolveId] = useState<string | null>(null);
  const [resolveType, setResolveType] = useState<ResolveType>('bad_debt_writeoff');
  const [resolveReason, setResolveReason] = useState('');
  const [resolveFee, setResolveFee] = useState('');
  const [resolveFactor, setResolveFactor] = useState('');
  const closeResolve = () => {
    setResolveId(null);
    setResolveReason('');
    setResolveFee('');
    setResolveFactor('');
  };
  // The single open "Halda" popover (Looja / Kommentaar / Prioriteet / tegevused) —
  // one controlled id at a time (mirrors the period/split popover pattern). Folding
  // the comment + priority editors + actions in here is what keeps the register
  // scroll-free: the row carries only the dense compact cells (F3).
  const [openActionId, setOpenActionId] = useState<string | null>(null);
  // The register's delayed settlement/document hover annotation — table-lens parity
  // with the calendar (L14): BOTH lenses open the SAME GroupHoverCard / DocHoverCard
  // after the SAME hover-intent dwell (`useHoverIntent`), never a lens-local tooltip.
  // Two anchors (group vs single) mirror the calendar so the intent payload — not a
  // render-time lookup — decides which card opens (keeps the decision fully testable).
  const [groupHover, setGroupHover] = useState<HoverPos | null>(null);
  const [docHover, setDocHover] = useState<HoverPos | null>(null);
  const hoverIntent = useHoverIntent<{ pos: HoverPos; isGroup: boolean }>(({ pos, isGroup }) => {
    if (isGroup) {
      setGroupHover(pos);
      setDocHover(null);
    } else {
      setDocHover(pos);
      setGroupHover(null);
    }
  });
  const clearHover = () => {
    hoverIntent.cancel();
    setGroupHover(null);
    setDocHover(null);
  };
  // The single date the bulk actions apply (mirrors the per-row picker pattern).
  const [bulkDate, setBulkDate] = useState(today);
  const [scopeOn, setScopeOn] = useState(true);
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  // The "Halda" popover flips UP for rows low in the scroll body so it is never
  // clipped by the overflow-auto table — geometry decided by a pure helper (F1).
  const scrollRef = useRef<HTMLDivElement>(null);
  // Reveal a "locate" target row (scroll + flash) via the SAME shared hook the calendar
  // and matrix use, so locate lands identically in every lens (L14).
  useLocateScroll(scrollRef, activeMatch);
  const [openUp, setOpenUp] = useState(false);
  const openActions = (id: string, trigger: HTMLElement) => {
    const c = scrollRef.current!.getBoundingClientRect();
    const t = trigger.getBoundingClientRect();
    setOpenUp(
      popoverOpensUp({
        triggerTop: t.top,
        triggerBottom: t.bottom,
        containerTop: c.top,
        containerBottom: c.bottom,
        preferredHeight: 320,
      }),
    );
    setOpenActionId((cur) => (cur === id ? null : id));
  };

  const hasActions = Boolean(onReschedule || onMarkPaid || onSplit || onResolve);
  // A sticky select column + the lead/money/tail document columns + a sticky "Halda"
  // column; `colCount` keeps the empty-state colSpan correct (one collapsed "Summa"
  // column, or the three Neto · KM · Bruto columns when expanded).
  const colCount = LEAD_COLUMNS.length + (summaExpanded ? 3 : 1) + TAIL_COLUMNS.length + 2;

  // The shared reporting basis re-places every row (Tähtaeg = effective date):
  // accrual pins document dates, so carry-forward flags vanish there (L14 shared).
  const rows = useMemo(() => entries.map((e) => toRow(e, today, basis)), [entries, today, basis]);

  // The faceted "+ Filter" surface is the SAME component the toolbar uses; Projekt
  // and Dokumendi koostaja are built from the entries in view (buildFacets).
  const facets = useMemo(() => buildFacets(entries), [entries]);
  const counts = useMemo(() => {
    const c: Partial<Record<FilterKey, Record<string, number>>> = {};
    for (const f of facets) c[f.key] = facetCounts(entries, f.key, today);
    return c;
  }, [facets, entries, today]);

  const toggleFilter = (key: FilterKey, value: string) =>
    setFilters((f) => {
      const next = new Set(f.sel[key]);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return { ...f, sel: { ...f.sel, [key]: next } };
    });
  const setAmount = (which: 'min' | 'max', value: string) =>
    setFilters((f) => ({ ...f, [which === 'min' ? 'amountMin' : 'amountMax']: value }));
  const clearFilters = () => setFilters(emptyFilters());

  const filtered = rows.filter((row) => {
    // The drill-in scope is the only register-local narrowing (the navigation context
    // you opened the register WITH); search · Suund · Staatus · the faceted panel all
    // live in the shared `filters` now (R7 / L14), so ONE matcher covers them.
    if (scope && scopeOn && !scope.match(row)) return false;
    return entryMatchesFilters(row.e, filters, today, basis);
  });

  const sorted = [...filtered].sort((a, b) => {
    const av = sortVal(a, sortKey, mode);
    const bv = sortVal(b, sortKey, mode);
    const cmp =
      typeof av === 'number' && typeof bv === 'number'
        ? av - bv
        : String(av).localeCompare(String(bv), 'et');
    return sortDir === 'asc' ? cmp : -cmp;
  });

  // "Leia üles" (locate) — keep the shared filter intact and instead reveal the first
  // matching document in place (scroll + flash) via the workspace, so the SAME locate
  // works in the calendar and matrix too (L14). Matching reuses the shared, hyphen/space
  // tolerant filter matcher; no money or row state is touched here.
  const runLocate = () => {
    const term = locateQuery.trim();
    if (term === '') return;
    const hit = sorted.find((r) => entryMatchesFilters(r.e, { ...emptyFilters(), search: term }, today));
    if (hit) locateEntry(hit.e.id);
  };

  // Every monetary total is finance-core's sum over the exact rows on screen. The footer
  // "Kokku" is the SIGNED, prepayment-netted cash-flow total — `signedEffectiveSumEntries`
  // adds income +, cost − over each row's EFFECTIVE amount, so credit notes (stored
  // negative) net out (item 2) AND a lõpparve funded by a received ettemaksuarve is not
  // double-counted with the prepayment that already carried that cash (L9). Paid /
  // outstanding come from `effectiveSettlementRatioSummary` (the ONE prepayment-netted
  // settlement aggregator) — same basis as "Kokku" — never a component-side subtraction (L9).
  const signedTotal = signedEffectiveSumEntries(sorted.map((row) => row.e) as CoreEntry[]);
  const settle = effectiveSettlementRatioSummary(sorted.map((row) => row.e));
  // The footer "Jääk" is the SIGNED net outstanding (income + / cost −) — the true
  // tulu − kulu still-open effect of the rows on screen, NOT an unsigned magnitude
  // sum that would overstate a mixed register. finance-core owns the sign (L9 / L14).
  const signedOutstanding = signedOutstandingSum(sorted.map((row) => row.e));
  // T4 (display only) — in the expanded Neto · KM · Bruto block KM is DATA, shown
  // de-emphasised, and the column matching the active NET/GROSS toggle is emphasised
  // (v1.4); collapsed, the single "Summa" figure is labelled with the active mode so it
  // is never ambiguous. finance-core still owns every cent (L9).
  const modeLabel = mode === 'net' ? 'Neto' : 'Bruto';
  const netCellEmph = mode === 'net' ? 'bg-[hsl(var(--primary))]/[0.06]' : undefined;
  const grossCellEmph = mode === 'gross' ? 'bg-[hsl(var(--primary))]/[0.06]' : undefined;
  const netHeadCls = mode === 'net' ? 'text-foreground' : 'text-muted-foreground';
  const grossHeadCls = mode === 'gross' ? 'text-foreground' : 'text-muted-foreground';
  const KM_CELL = 'opacity-60';
  // The scope-header breakdown (count + incoming/outgoing + actual-vs-expected) over
  // the documents on screen — the expected-vs-actual figures the calendar-day overlay
  // used to show, now generalised to ANY register scope. Pure finance-core (L9).
  const scopeSummary = summarizeLedgerScope(sorted.map((row) => row.e));

  // The ticked documents (across every entry in view) and their finance-core tally — the
  // selection total is the SIGNED, prepayment-netted cash-flow sum (`signedEffectiveSumEntries`,
  // like the footer), so a mixed selection shows its true net cash effect, never double-counts
  // a prepaid lõpparve with its ettemaksuarve, and reconciles with "Kokku" (item 2); never a
  // component-side add (L9).
  const selectedEntries = entries.filter((e) => selectedIdSet.has(e.id));
  const selectedTotal = signedEffectiveSumEntries(selectedEntries as CoreEntry[]);

  // Select-all reflects the visible rows: ticking adds every row in view, unticking
  // removes them (a partial selection shows the box indeterminate).
  const visibleIds = sorted.map((row) => row.e.id);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIdSet.has(id));
  const someVisibleSelected = visibleIds.some((id) => selectedIdSet.has(id));
  const toggleAllVisible = () => {
    if (allVisibleSelected) {
      const drop = new Set(visibleIds);
      setSelected((ids) => ids.filter((id) => !drop.has(id)));
    } else {
      setSelected((ids) => Array.from(new Set([...ids, ...visibleIds])));
    }
  };

  // The bulk buttons render ONLY when their handler is supplied (see the selection
  // bar), so the handler is a guaranteed invariant here — a `!` keeps the branch graph
  // clean (an `if (handler)` arm would be unreachable and break 100% coverage).
  // A payroll net-wage row (`payroll:<M>:net`) is SKIPPED: its jääk is event-
  // sourced from per-employee payments (0037), so a whole-entry mark-paid would
  // double-count — settle it employee-by-employee in the entry detail instead.
  // An already-settled row (no outstanding gap, finance-core `outstandingOf`) is
  // ALSO skipped — the server would 400 the whole batch otherwise. The bulk action
  // always books the FULL gap; a partial is a per-row / per-entry decision.
  const bulkEligible = selectedEntries.filter(
    (e) => payrollNetEntryMonth(e.externalId) === null && outstandingOf(e) > 0,
  );
  // Bulk mark-paid asks first ("kas ikka soovid?") — a two-step confirm like the
  // per-row panel, listing the count + date + the sync-supersede note.
  const [bulkPaidConfirm, setBulkPaidConfirm] = useState(false);
  const bulkMarkPaid = () => {
    for (const e of bulkEligible) onMarkPaid!(e.id, { paidOn: bulkDate });
    setBulkPaidConfirm(false);
  };
  const bulkReschedule = () => {
    for (const e of selectedEntries) onReschedule!(e.id, bulkDate);
  };

  // The inline editor renders ONLY when `onComment` is supplied, so it is a guaranteed
  // invariant here (`!`); the sole branch is "did the text actually change?".
  const commitComment = (id: string, raw: string, current: string | null) => {
    const next = raw.trim() === '' ? null : raw.trim();
    if (next !== current) onComment!(id, next);
  };

  const toggleSort = (k: SortKey) => {
    if (k === sortKey) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(k);
      setSortDir('asc');
    }
  };

  // Export EXACTLY the rows on screen (after scope + filter + sort) to a CSV (E2). The
  // builder only SELECTS + formats the figures finance-core already produced (no money
  // math here — L9); the download is a thin DOM side-effect.
  const exportCsv = () => {
    const csv = buildLedgerCsv(
      sorted.map((row) => ({
        suund: row.suund,
        dok: row.dok,
        osapool: row.osapool,
        liik: row.liik,
        project: row.project,
        // The CSV mirrors the SCREEN: a carried-forward row exports its ORIGINAL due
        // day (what the Tähtaeg column shows/sorts), not the shared "today".
        tahtaeg: row.overdueAge !== null ? row.overdueAge.since : row.tahtaeg,
        docDate: row.docDate,
        money: row.e.money,
        kaetud: totalDocumentOffsetOf(row.e),
        tasutud: row.tasutud,
        jaak: row.jaak,
        status: row.status,
      })),
    );
    downloadLedgerCsv(`rahavoog-register-${today}.csv`, csv);
  };

  // A generic sortable text/settlement header (lead + tail columns).
  const sortGlyph = (key: SortKey) => (sortKey === key ? (sortDir === 'asc' ? '▲' : '▼') : '↕');
  function HeadCell({ col }: { col: LedgerColumn }) {
    return (
      <th
        onClick={() => toggleSort(col.key)}
        title={col.headTitle ?? 'Sorteeri'}
        className={cn(
          'sticky top-0 z-20 cursor-pointer select-none overflow-hidden text-ellipsis whitespace-nowrap border-b border-border bg-card px-2.5 py-2 font-medium text-muted-foreground hover:text-foreground',
          col.align === 'right' ? 'text-right' : 'text-left',
        )}
      >
        <span className="inline-flex items-center gap-1">
          {col.label}
          {col.marker && (
            <span className="font-bold text-[hsl(var(--vatmode))]" title="Alati bruto (koos KM-iga)">
              {col.marker}
            </span>
          )}
          <span className="text-[8px] text-muted-foreground/70">{sortGlyph(col.key)}</span>
        </span>
      </th>
    );
  }

  // The money header. COLLAPSED: ONE "Summa" column in the shared workspace mode (Neto
  // or Bruto), sortable by the mode-selected field via `sortVal('summa', mode)`, plus a
  // caret that EXPANDS it. EXPANDED: the Neto · KM · Bruto breakdown — three sortable
  // columns over the SAME `Money` (KM is DATA, not a money-mode — v1.4), the first
  // carrying the caret that collapses it again. Expand/collapse is presentation only.
  function MoneyHead() {
    if (!summaExpanded) {
      return (
        <th
          onClick={() => toggleSort('summa')}
          title="Sorteeri summa järgi"
          className="sticky top-0 z-20 cursor-pointer select-none whitespace-nowrap border-b border-border bg-card px-2.5 py-2 text-right font-medium text-muted-foreground hover:text-foreground"
        >
          <span className="inline-flex items-center justify-end gap-1">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setSummaExpanded(true);
              }}
              aria-label="Laienda summa"
              title="Laienda summa — Neto · KM · Bruto (sama raha, eraldi veergudes)"
              className="rounded px-1 text-muted-foreground/70 hover:text-foreground"
            >
              ⊞
            </button>
            Summa
            <span
              className={cn(
                'font-bold',
                mode === 'net' ? 'text-[hsl(var(--netmode))]' : 'text-[hsl(var(--vatmode))]',
              )}
              title={modeLabel}
            >
              {mode === 'net' ? 'N' : 'B'}
            </span>
            <span className="text-[8px] text-muted-foreground/70">{sortGlyph('summa')}</span>
          </span>
        </th>
      );
    }
    return (
      <>
        <th
          onClick={() => toggleSort('net')}
          title="Sorteeri neto järgi"
          className={cn(
            'sticky top-0 z-20 cursor-pointer select-none whitespace-nowrap border-b border-border bg-card px-2.5 py-2 text-right font-medium hover:text-foreground',
            netHeadCls,
            netCellEmph,
          )}
        >
          <span className="inline-flex items-center justify-end gap-1">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setSummaExpanded(false);
              }}
              aria-label="Ahenda summa"
              title="Ahenda summa — üks veerg (aktiivne raha vaade)"
              className="rounded px-1 text-muted-foreground/70 hover:text-foreground"
            >
              ⊟
            </button>
            Neto
            <span className="text-[8px] text-muted-foreground/70">{sortGlyph('net')}</span>
          </span>
        </th>
        <th
          onClick={() => toggleSort('vat')}
          title="Sorteeri KM järgi"
          className="sticky top-0 z-20 cursor-pointer select-none whitespace-nowrap border-b border-border bg-card px-2.5 py-2 text-right font-medium text-muted-foreground/60 hover:text-foreground"
        >
          <span className="inline-flex items-center justify-end gap-1">
            KM
            <span className="text-[8px] text-muted-foreground/70">{sortGlyph('vat')}</span>
          </span>
        </th>
        <th
          onClick={() => toggleSort('gross')}
          title="Sorteeri bruto järgi"
          className={cn(
            'sticky top-0 z-20 cursor-pointer select-none whitespace-nowrap border-b border-border bg-card px-2.5 py-2 text-right font-medium hover:text-foreground',
            grossHeadCls,
            grossCellEmph,
          )}
        >
          <span className="inline-flex items-center justify-end gap-1">
            Bruto
            <span className="text-[8px] text-muted-foreground/70">{sortGlyph('gross')}</span>
          </span>
        </th>
      </>
    );
  }

  // The per-row money cell(s). A lõpparve funded by a received prepayment (an ETT↔ARV
  // link) is shown NETTED so the row never double-counts the euro its ettemaksuarve
  // already carried — the netting is finance-core's (`signedEffectiveSumEntries` /
  // `totalDocumentOffsetOf`), never a component subtraction (L9): a FULLY-covered
  // final shows its face amount struck through with "kaetud ettemaksuga" and contributes
  // 0 to every total; a PARTIALLY-covered one shows its remaining amount with the offset
  // noted. The REVERSE side reads the same way: an ettemaksuarve whose lõpparve was
  // cash-paid directly is covered too and its cue says "kaetud lõpparvega" — the side
  // is finance-core's (`prepaymentCoveredByFinalAmountOf`), never re-derived here.
  // Credit-note netting reads through the SAME total: a credited invoice's cancelled
  // share says "krediteeritud", an applied kreeditarve says "tasaarveldatud" — both
  // resolved by finance-core (`creditNoteOffsetAmountOf` / `creditNoteAppliedAmountOf`).
  // COLLAPSED: ONE amount in the shared workspace mode, SIGNED + neutral
  // (`pickSum`). EXPANDED: the Neto · KM · Bruto breakdown of the same signed `Money`.
  // Either way the component only SELECTS + formats figures finance-core produced (L9).
  function MoneyCells({ row }: { row: LedgerRow }) {
    const signed = signedSumEntries([row.e]);
    const offset = totalDocumentOffsetOf(row.e);
    const coveredByFinal = prepaymentCoveredByFinalAmountOf(row.e) > 0;
    const creditApplied = creditNoteAppliedAmountOf(row.e) < 0; // the KRE side (signed −)
    const credited = creditNoteOffsetAmountOf(row.e) > 0; // the credited-invoice side
    const covered = offset !== 0;
    const effSigned = covered ? signedEffectiveSumEntries([row.e] as CoreEntry[]) : signed;
    const fullyCovered = covered && effSigned.gross === 0;
    const coverCue = creditApplied
      ? 'tasaarveldatud'
      : credited
        ? 'krediteeritud'
        : coveredByFinal
          ? 'kaetud lõpparvega'
          : 'kaetud ettemaksuga';
    const offsetCue = creditApplied
      ? 'tasaarveldatud'
      : credited
        ? 'krediteeritud'
        : coveredByFinal
          ? 'lõpparvega kaetud'
          : 'ettemaks';
    // A forecast row's amount reads italic in EVERY branch (collapsed / covered /
    // expanded), the same always-on cue the calendar chip amount uses (L14) — display
    // only, no maths (L9).
    const amountItalic = forecastAmountClass(row.certainty);
    if (!summaExpanded) {
      if (fullyCovered) {
        return (
          <td className={cn('px-2.5 py-1.5 text-right', amountItalic)}>
            <span className="line-through opacity-60">
              <PlainAmount cents={pickSum(signed, mode)} tone="normal" />
            </span>
            <span className="mt-0.5 block text-[10px] font-normal text-muted-foreground">
              {coverCue}
            </span>
          </td>
        );
      }
      if (covered) {
        return (
          <td className={cn('px-2.5 py-1.5 text-right', amountItalic)}>
            <PlainAmount cents={pickSum(effSigned, mode)} tone="strong" />
            <span className="mt-0.5 block text-[10px] font-normal text-muted-foreground">
              −{formatEur(Math.abs(offset))} {offsetCue}
            </span>
          </td>
        );
      }
      return (
        <td className={cn('px-2.5 py-1.5 text-right', amountItalic)}>
          <PlainAmount cents={pickSum(signed, mode)} tone="strong" />
        </td>
      );
    }
    const shown = fullyCovered ? signed : effSigned;
    const struck = fullyCovered && 'line-through opacity-60';
    return (
      <>
        <td className={cn('px-2.5 py-1.5 text-right', netCellEmph, struck, amountItalic)}>
          <PlainAmount cents={shown.net} tone={mode === 'net' ? 'strong' : 'normal'} />
        </td>
        <td className={cn('px-2.5 py-1.5 text-right', KM_CELL, struck, amountItalic)}>
          <PlainAmount cents={shown.vat} tone="faint" />
        </td>
        <td className={cn('px-2.5 py-1.5 text-right', grossCellEmph, struck, amountItalic)}>
          <PlainAmount cents={shown.gross} tone={mode === 'gross' ? 'strong' : 'normal'} />
        </td>
      </>
    );
  }

  // Two small group-aware rows for the grouped register (E1). A header introduces each
  // group; a subtotal closes it — and the subtotal is the SAME finance-core aggregate the
  // footer uses (`signedEffectiveSumEntries` prepayment-netted signed total +
  // `signedOutstandingSum` net "Jääk" + `effectiveSettlementRatioSummary` paid), so a group
  // never sums money itself (L9), never double-counts an ETT↔ARV pair, and reconciles with
  // "Kokku" (L14).
  function GroupHeaderRow({ group }: { group: LedgerGroup<LedgerRow> }) {
    return (
      <tr data-testid="ledger-group-header" className="[&>td]:border-y [&>td]:border-border/60">
        <td className="sticky left-0 z-10 bg-card px-2 py-1.5" />
        <td colSpan={colCount - 1} className="bg-muted/40 px-2.5 py-1.5">
          <span className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-foreground">
            {group.suund && (
              <span
                aria-hidden="true"
                className={cn(
                  'inline-block h-2.5 w-2.5 rounded-[2px]',
                  group.suund === 'income' ? 'bg-[hsl(var(--income))]' : 'bg-[hsl(var(--cost))]',
                )}
              />
            )}
            {group.label}
            <span className="font-normal normal-case text-muted-foreground">
              {' '}· {group.rows.length} kirjet
            </span>
          </span>
        </td>
      </tr>
    );
  }

  function SubtotalRow({ group }: { group: LedgerGroup<LedgerRow> }) {
    const groupEntries = group.rows.map((row) => row.e);
    const groupSigned = signedEffectiveSumEntries(groupEntries as CoreEntry[]);
    const groupSettle = effectiveSettlementRatioSummary(groupEntries);
    return (
      <tr
        data-testid="ledger-subtotal"
        className="bg-muted/20 font-medium [&>td]:border-y [&>td]:border-border/60"
      >
        <td className="sticky left-0 z-10 bg-card px-2 py-1.5" />
        <td
          colSpan={LEAD_COLUMNS.length}
          className="whitespace-nowrap px-2.5 py-1.5 text-muted-foreground"
        >
          Vahesumma · {group.label} · {group.rows.length} kirjet
        </td>
        {summaExpanded ? (
          <>
            <td className={cn('px-2.5 py-1.5 text-right tabular-nums', netCellEmph)}>
              <SignedEur cents={groupSigned.net} />
            </td>
            <td className={cn('px-2.5 py-1.5 text-right tabular-nums', KM_CELL)}>
              <SignedEur cents={groupSigned.vat} />
            </td>
            <td className={cn('px-2.5 py-1.5 text-right tabular-nums', grossCellEmph)}>
              <SignedEur cents={groupSigned.gross} />
            </td>
          </>
        ) : (
          <td className="px-2.5 py-1.5 text-right tabular-nums">
            <span className="mr-1 text-[10px] font-normal text-muted-foreground/70">{modeLabel}</span>
            <SignedEur cents={pickSum(groupSigned, mode)} />
          </td>
        )}
        <td className="px-2.5 py-1.5 text-right tabular-nums">
          <SignedEur cents={signedOutstandingSum(groupEntries)} />
        </td>
        {/* Allikas — no subtotal (rank, not an amount); empty cell keeps the alignment. */}
        <td className="px-2.5 py-1.5" />
        <td className="px-2.5 py-1.5 text-right tabular-nums text-[hsl(var(--settled))]">
          {formatEur(groupSettle.paid)}
          <span className="ml-1 text-[10px] text-muted-foreground">
            {formatSettledPct(groupSettle.ratioBps)}
          </span>
        </td>
        <td className="sticky right-0 z-10 border-l border-border/60 bg-card px-2.5 py-1.5" />
      </tr>
    );
  }

  // The flat render list: ungrouped it is just the rows; grouped it interleaves a header
  // and a subtotal around each group's rows (E1) — ONE row-render path consumes it below.
  type RenderItem =
    | { kind: 'row'; row: LedgerRow }
    | { kind: 'group'; group: LedgerGroup<LedgerRow> }
    | { kind: 'subtotal'; group: LedgerGroup<LedgerRow> };
  const items: RenderItem[] =
    groupBy === 'none'
      ? sorted.map((row): RenderItem => ({ kind: 'row', row }))
      : groupLedgerRows(sorted, groupBy).flatMap<RenderItem>((group) => [
          { kind: 'group', group },
          ...group.rows.map((row): RenderItem => ({ kind: 'row', row })),
          { kind: 'subtotal', group },
        ]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Kõik dokumendid"
      className="absolute inset-0 z-50 flex items-stretch justify-center bg-background/75 p-6 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="flex w-full max-w-none flex-col overflow-hidden rounded-xl border border-border bg-card shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-foreground">
              <span>{scope && scopeOn ? 'Dokumendid' : 'Kõik dokumendid'}</span>
              <span aria-hidden="true"> —</span>
            </span>
            <PeriodPicker />
            <span className="text-[11px] text-muted-foreground">{sorted.length} kirjet</span>
          </div>
          <div className="flex items-center gap-1.5">
            {onAddEntry && (
              // The register's lens-appropriate "+" — opens the ONE shared create dialog
              // (seeded with today). Same create surface as the calendar day "+" and the
              // table line "+", so create is reachable from every lens (L14).
              <button
                type="button"
                onClick={() => onAddEntry({ date: today })}
                aria-label="Lisa kirje"
                title="Lisa uus kirje"
                className="rounded-md border border-primary/40 bg-primary/10 px-2 py-1 text-[11px] font-medium text-primary transition-colors hover:bg-primary/20"
              >
                <span aria-hidden>+</span> Lisa
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              aria-label="Sulge"
              className="rounded-md px-2 py-1 text-sm text-muted-foreground hover:bg-accent hover:text-foreground"
            >
              ✕
            </button>
          </div>
        </div>

        <div
          className="flex flex-wrap items-center gap-x-4 gap-y-1 border-b border-border bg-background/40 px-4 py-1.5 text-[11px]"
          data-testid="ledger-scope-summary"
        >
          <span className="text-muted-foreground">{scopeSummary.count} dokumenti</span>
          <span className="flex items-center gap-1">
            <span className="text-muted-foreground">Tulu</span>
            <span className="tabular-nums font-semibold text-[hsl(var(--income))]">
              +{formatEur(scopeSummary.income.gross)}
            </span>
          </span>
          <span className="flex items-center gap-1">
            <span className="text-muted-foreground">Kulu</span>
            <span className="tabular-nums font-semibold text-[hsl(var(--cost))]">
              −{formatEur(scopeSummary.cost.gross)}
            </span>
          </span>
          <span className="flex items-center gap-1">
            <span className="text-muted-foreground" title="Tegelik — laekunud / makstud raha">
              Tegelik
            </span>
            <span className="tabular-nums font-semibold text-foreground">{formatEur(scopeSummary.settlement.paid)}</span>
          </span>
          <span className="flex items-center gap-1">
            <span className="text-muted-foreground" title="Oodatud — veel laekumata / maksmata jääk">
              Oodatud
            </span>
            <span className="tabular-nums font-semibold text-foreground">
              {formatEur(scopeSummary.settlement.outstanding)}
            </span>
          </span>
        </div>

        <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-2">
          <input
            value={searchBehavior === 'filter' ? q : locateQuery}
            onChange={(e) =>
              searchBehavior === 'filter' ? setSearch(e.target.value) : setLocateQuery(e.target.value)
            }
            onKeyDown={(e) => {
              if (searchBehavior === 'locate' && e.key === 'Enter') runLocate();
            }}
            aria-label="Otsi dokumente"
            placeholder={
              searchBehavior === 'filter'
                ? 'Filtreeri: dokument, pakkumine, osapool, projekt, looja, kommentaar…'
                : 'Leia üles: dokument, osapool… (Enter)'
            }
            className="h-7 w-64 rounded-md border border-border bg-background px-2.5 text-xs text-foreground outline-none placeholder:text-muted-foreground focus:border-primary/60"
          />
          <div role="group" aria-label="Otsingu režiim" className="flex overflow-hidden rounded-md border border-border">
            {(['filter', 'locate'] as const).map((b) => (
              <button
                key={b}
                type="button"
                onClick={() => setSearchBehavior(b)}
                aria-pressed={searchBehavior === b}
                title={
                  b === 'filter'
                    ? 'Filtreeri — kitsenda KÕIKI vaateid otsingule (L14)'
                    : 'Leia üles — hoia filter, keri ja tõsta esile esimene vaste'
                }
                className={cn(
                  'px-2 py-0.5 text-[11px] transition-colors',
                  searchBehavior === b
                    ? 'bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))]'
                    : 'bg-background text-muted-foreground hover:text-foreground',
                )}
              >
                {b === 'filter' ? 'Filtreeri' : 'Leia'}
              </button>
            ))}
          </div>
          {scope && (
            <button
              type="button"
              onClick={() => setScopeOn((v) => !v)}
              title={scopeOn ? 'Eemalda filter — näita kõiki kirjeid' : 'Taasta filter'}
              className={cn(
                'rounded border px-2 py-0.5 text-[11px] transition-colors',
                scopeOn
                  ? 'border-[hsl(var(--primary))]/60 bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))]'
                  : 'border-border bg-background text-muted-foreground line-through hover:text-foreground',
              )}
            >
              {scope.label} {scopeOn ? '✕' : '+'}
            </button>
          )}
          <button
            type="button"
            onClick={() => setSelectedOnly((v) => !v)}
            aria-pressed={selectedOnly}
            title="Näita ainult valitud dokumente — kitsendab kalendri, tabeli ja registri (L14)"
            className={cn(
              'rounded border px-2 py-0.5 text-[11px] transition-colors',
              selectedOnly
                ? 'border-primary/60 bg-primary/15 text-primary'
                : 'border-border bg-background text-muted-foreground hover:text-foreground',
            )}
          >
            Ainult valitud
          </button>
          <span className="ml-1 text-[10px] uppercase tracking-wide text-muted-foreground">Suund</span>
          {(['all', 'income', 'cost'] as const).map((s) => {
            const on = s === 'all' ? dirSel.size === 0 : dirSel.has(s);
            return (
              <button
                key={s}
                type="button"
                aria-pressed={on}
                onClick={() => toggleQuickFacet('direction', s === 'all' ? null : s)}
                className={cn(
                  'rounded border px-2 py-0.5 text-[11px] transition-colors',
                  on
                    ? 'border-primary/60 bg-primary/15 text-primary'
                    : 'border-border bg-background text-muted-foreground hover:text-foreground',
                )}
              >
                {s === 'all' ? 'Kõik' : s === 'income' ? 'Tulu' : 'Kulu'}
              </button>
            );
          })}
          <span className="ml-2 text-[10px] uppercase tracking-wide text-muted-foreground">Staatus</span>
          {STATUS_FILTERS.map((s) => {
            const on = s === 'all' ? statSel.size === 0 : statSel.has(s);
            return (
              <button
                key={s}
                type="button"
                aria-pressed={on}
                title="Vali mitu staatust korraga — iga kiip lülitub eraldi; „Kõik“ tühjendab"
                onClick={() => toggleQuickFacet('settlementStatus', s === 'all' ? null : s)}
                className={cn(
                  'rounded border px-2 py-0.5 text-[11px] transition-colors',
                  on
                    ? 'border-primary/60 bg-primary/15 text-primary'
                    : 'border-border bg-background text-muted-foreground hover:text-foreground',
                )}
              >
                {statusFilterLabel(s)}
              </button>
            );
          })}
          <button
            type="button"
            aria-label="Rohkem filtreid"
            aria-expanded={filterPanelOpen}
            onClick={() => setFilterPanelOpen((o) => !o)}
            className={cn(
              'flex items-center gap-1 rounded-md border px-2 py-0.5 text-[11px] transition-colors',
              filterCount(filters) > 0
                ? 'border-primary/60 bg-primary/15 text-primary'
                : 'border-border bg-background text-muted-foreground hover:text-foreground',
            )}
          >
            <span className={cn('text-[10px] transition-transform', filterPanelOpen && 'rotate-90')}>▸</span> Rohkem filtreid
            {filterCount(filters) > 0 && (
              <span className="rounded bg-primary/25 px-1 text-[10px] font-semibold">
                {filterCount(filters)}
              </span>
            )}
          </button>
          <div className="flex items-center gap-1.5" data-testid="ledger-groupby">
            <span className="ml-1 text-[10px] uppercase tracking-wide text-muted-foreground">Rühmita</span>
            {LEDGER_GROUP_OPTIONS.map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => setGroupBy(key)}
                aria-pressed={groupBy === key}
                title="Rühmita register — alamread + vahesumma (sama jagatud finance-core summa)"
                className={cn(
                  'rounded border px-2 py-0.5 text-[11px] transition-colors',
                  groupBy === key
                    ? 'border-primary/60 bg-primary/15 text-primary'
                    : 'border-border bg-background text-muted-foreground hover:text-foreground',
                )}
              >
                {label}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={exportCsv}
            title="Ekspordi nähtavad read CSV-na (avaneb Excelis)"
            className="ml-auto inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-0.5 text-[11px] text-muted-foreground transition-colors hover:text-foreground"
          >
            ↧ Ekspordi CSV
          </button>
        </div>

        {filterPanelOpen && (
          <FilterPanel
            facets={facets}
            filters={filters}
            counts={counts}
            onToggle={toggleFilter}
            onAmount={setAmount}
            onClear={clearFilters}
            onClose={() => setFilterPanelOpen(false)}
          />
        )}

        {filterCount(filters) > 0 && (
          <div className="flex flex-wrap items-center gap-1.5 border-b border-border px-4 py-2">
            <FilterChipBar
              facets={facets}
              filters={filters}
              onToggle={toggleFilter}
              onAmount={setAmount}
              onClear={clearFilters}
            />
          </div>
        )}

        {selectedEntries.length > 0 && (
          <div
            className="flex flex-wrap items-center gap-x-4 gap-y-1.5 border-b border-border bg-primary/5 px-4 py-2 text-[11px]"
            data-testid="ledger-selection-bar"
          >
            <span className="font-semibold text-primary">{selectedEntries.length} valitud</span>
            <span className="flex items-center gap-1">
              <span className="text-muted-foreground">Neto</span>
              <SignedEur cents={selectedTotal.net} />
            </span>
            <span className="flex items-center gap-1">
              <span className="text-muted-foreground">KM</span>
              <SignedEur cents={selectedTotal.vat} />
            </span>
            <span className="flex items-center gap-1">
              <span className="text-muted-foreground">Bruto</span>
              <SignedEur cents={selectedTotal.gross} />
            </span>
            {hasActions && (
              <div className="ml-2 flex items-center gap-1.5">
                <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Tegevus</span>
                <EtDateInput
                  value={bulkDate}
                  onChangeIso={setBulkDate}
                  aria-label="Hulgitegevuse kuupäev"
                  title="Vali kuupäev hulgitegevuseks (kanna edasi / märgi makstuks)"
                  className="px-1 py-0.5 text-[10px]"
                />
                {onReschedule && (
                  <button
                    type="button"
                    onClick={bulkReschedule}
                    title="Kanna valitud dokumendid valitud kuupäeva"
                    className="rounded border border-border px-1.5 py-0.5 text-[10px] text-foreground transition-colors hover:bg-accent"
                  >
                    Kanna edasi
                  </button>
                )}
                {onMarkPaid &&
                  (bulkPaidConfirm ? (
                    <span className="flex items-center gap-1.5" data-testid="bulk-mark-paid-confirm">
                      <span className="text-[10px] text-foreground">
                        Märgid {bulkEligible.length} dokumenti makstuks ({formatEtDate(bulkDate)},
                        kogu jääk) — hilisem sünkroonitud makse prioritiseerib käsitsi kirje üle.
                        Kas soovid jätkata?
                      </span>
                      <button
                        type="button"
                        onClick={bulkMarkPaid}
                        className="rounded border border-[hsl(var(--income))]/50 px-1.5 py-0.5 text-[10px] font-medium text-[hsl(var(--income))] transition-colors hover:bg-[hsl(var(--income))]/10"
                      >
                        Kinnita
                      </button>
                      <button
                        type="button"
                        onClick={() => setBulkPaidConfirm(false)}
                        className="rounded border border-border px-1.5 py-0.5 text-[10px] text-muted-foreground transition-colors hover:text-foreground"
                      >
                        Tühista
                      </button>
                    </span>
                  ) : (
                    <button
                      type="button"
                      disabled={bulkEligible.length === 0}
                      onClick={() => setBulkPaidConfirm(true)}
                      title="Märgi valitud dokumendid tasutuks (valitud kuupäevaga, kogu jäägi ulatuses); arveldatud ja palgaread jäetakse vahele"
                      className="rounded border border-[hsl(var(--income))]/50 px-1.5 py-0.5 text-[10px] text-[hsl(var(--income))] transition-colors hover:bg-[hsl(var(--income))]/10 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      Märgi makstuks
                    </button>
                  ))}
              </div>
            )}
            <button
              type="button"
              onClick={clearSelection}
              className="ml-auto rounded border border-border px-1.5 py-0.5 text-[10px] text-muted-foreground transition-colors hover:text-foreground"
            >
              Tühista valik
            </button>
          </div>
        )}

        <div ref={scrollRef} data-testid="ledger-scroll" className="min-h-0 flex-1 overflow-auto">
          <table className="w-full table-fixed border-separate border-spacing-0 text-xs">
            <colgroup>
              <col style={{ width: SELECT_W }} />
              {LEAD_COLUMNS.map((c) => (
                <col key={c.key} style={{ width: c.width }} />
              ))}
              {summaExpanded ? (
                <>
                  <col style={{ width: SUMMA_SUB_W }} />
                  <col style={{ width: SUMMA_SUB_W }} />
                  <col style={{ width: SUMMA_SUB_W }} />
                </>
              ) : (
                <col style={{ width: SUMMA_W }} />
              )}
              {TAIL_COLUMNS.map((c) => (
                <col key={c.key} style={{ width: c.width }} />
              ))}
              <col style={{ width: ACTION_W }} />
            </colgroup>
            <thead>
              <tr>
                <th className="sticky left-0 top-0 z-30 border-b border-border bg-card px-2 py-2 align-middle">
                  <input
                    type="checkbox"
                    aria-label="Vali kõik nähtavad"
                    checked={allVisibleSelected}
                    ref={(el) => {
                      if (el) el.indeterminate = someVisibleSelected && !allVisibleSelected;
                    }}
                    onChange={toggleAllVisible}
                    className="h-3.5 w-3.5 cursor-pointer accent-[hsl(var(--primary))]"
                  />
                </th>
                {LEAD_COLUMNS.map((c) => (
                  <HeadCell key={c.key} col={c} />
                ))}
                <MoneyHead />
                {TAIL_COLUMNS.map((c) => (
                  <HeadCell key={c.key} col={c} />
                ))}
                <th className="sticky right-0 top-0 z-30 whitespace-nowrap border-b border-l border-border bg-card px-2.5 py-2 text-right font-medium text-muted-foreground">
                  Halda
                </th>
              </tr>
            </thead>
            <tbody>
              {sorted.length === 0 ? (
                <tr>
                  <td colSpan={colCount} className="px-2.5 py-6 text-center text-muted-foreground">
                    Kirjeid ei leitud
                  </td>
                </tr>
              ) : (
                items.map((item) => {
                  if (item.kind === 'group') {
                    return <GroupHeaderRow key={`grp:${item.group.id}`} group={item.group} />;
                  }
                  if (item.kind === 'subtotal') {
                    return <SubtotalRow key={`sub:${item.group.id}`} group={item.group} />;
                  }
                  const row = item.row;
                  const isSelected = selectedIdSet.has(row.e.id);
                  const dc = docCell(row.docType);
                  const lc = liikCell(row.category);
                  const pc = priorityCell(row.e.priority);
                  const proj = projectCompact(row.project);
                  const isOpen = openActionId === row.e.id;
                  // The prepayment-offset link (ETT↔ARV), if any — a discoverable inline
                  // jump to the OTHER document in the pair, the register-lens twin of the
                  // enriched detail's "Seotud dokumendid" panel (L14 parity: the linkage is
                  // reachable from BOTH lenses). Only when direct-open is wired.
                  const rel = onOpenEntry ? (row.e.relatedDocuments[0] ?? null) : null;
                  return (
                    <tr
                      key={row.e.id}
                      data-entry={row.e.id}
                      data-selected={isSelected ? 'true' : undefined}
                      // The whole row is the direct-open affordance (L14 parity with the
                      // calendar chip): clicking the row surface opens the shared document
                      // detail, but a click that lands on an interactive child (select box,
                      // Halda menu, the row-end direct-open icon, a form field) drives ITS
                      // own action instead. Keyboard parity: Enter/Space on the focused row.
                      role={onOpenEntry ? 'button' : undefined}
                      tabIndex={onOpenEntry ? 0 : undefined}
                      aria-label={onOpenEntry ? 'Ava dokumendivaade' : undefined}
                      onClick={
                        onOpenEntry
                          ? (ev) => {
                              if (isInteractiveTarget(ev.target as Element)) return;
                              onOpenEntry(row.e.id);
                            }
                          : undefined
                      }
                      onKeyDown={
                        onOpenEntry
                          ? (ev) => {
                              if (ev.target !== ev.currentTarget) return;
                              if (ev.key === 'Enter' || ev.key === ' ') {
                                ev.preventDefault();
                                onOpenEntry(row.e.id);
                              }
                            }
                          : undefined
                      }
                      className={cn(
                        'group [&>td]:border-b [&>td]:border-border/60 hover:bg-accent/30',
                        onOpenEntry && 'cursor-pointer',
                        forecastBaseClass(row.certainty),
                        forecastEmphasisClass(row.certainty, forecastEmphasis),
                        isSelected && 'bg-primary/10',
                      )}
                    >
                      <td className="sticky left-0 z-10 bg-card px-2 py-1.5 align-middle">
                        <input
                          type="checkbox"
                          aria-label={`Vali ${row.dok}`}
                          checked={isSelected}
                          onChange={() => toggleSelected(row.e.id)}
                          className="h-3.5 w-3.5 cursor-pointer accent-[hsl(var(--primary))]"
                        />
                      </td>
                      <td className="px-2.5 py-1.5">
                        <span
                          role="img"
                          aria-label={row.suund === 'income' ? 'Tulu' : 'Kulu'}
                          className={cn(
                            'inline-block h-2.5 w-2.5 rounded-[2px]',
                            row.suund === 'income' ? 'bg-[hsl(var(--income))]' : 'bg-[hsl(var(--cost))]',
                          )}
                        />
                      </td>
                      <td
                        title={dc.label ? `${dc.label} · ${row.dok}` : row.dok}
                        className="overflow-hidden whitespace-nowrap py-1.5 pl-1 pr-2.5"
                      >
                        <span className="flex items-center gap-1 overflow-hidden">
                          {dc.code && (
                            <span
                              className={cn(
                                'shrink-0 rounded px-1 py-0.5 text-[9px] font-medium',
                                docTypeBadgeCls(row.docType),
                              )}
                            >
                              {dc.code}
                            </span>
                          )}
                          <span
                            data-testid="ledger-dok"
                            className="overflow-hidden text-ellipsis font-mono text-[11px] text-muted-foreground"
                          >
                            {row.dok}
                          </span>
                          {pc && (
                            <span
                              aria-label={`Prioriteet: ${pc.label}`}
                              title={`Prioriteet: ${pc.label}`}
                              className={cn('shrink-0 rounded px-1 py-0.5 text-[9px] font-semibold', pc.cls)}
                            >
                              ⚑
                            </span>
                          )}
                          {rel && (
                            <button
                              type="button"
                              data-testid="ledger-relation-link"
                              aria-label={[relationCaption(rel), rel.relatedDocNumber]
                                .filter(Boolean)
                                .join(' · ')}
                              title={[relationCaption(rel), rel.relatedDocNumber]
                                .filter(Boolean)
                                .join(' · ')}
                              onClick={() => onOpenEntry!(rel.relatedEntryId)}
                              className="flex shrink-0 items-center gap-0.5 rounded px-1 leading-none text-muted-foreground hover:text-foreground"
                            >
                              <span className="text-[11px] leading-none" aria-hidden>
                                ⇄
                              </span>
                              <span className="font-mono text-[9px] leading-none">{relationLinkLabel(rel)}</span>
                            </button>
                          )}
                        </span>
                      </td>
                      <td
                        title={row.osapool}
                        className="overflow-hidden whitespace-nowrap px-2.5 py-1.5 text-foreground"
                      >
                        <span className="flex items-center gap-1.5 overflow-hidden">
                          <span className="overflow-hidden text-ellipsis">{row.osapool}</span>
                          {row.certainty === 'forecast' && (
                            <span title={FORECAST_PILL_TITLE} className={FORECAST_PILL_CLS}>
                              {FORECAST_PILL_LABEL}
                            </span>
                          )}
                        </span>
                      </td>
                      <td
                        title={
                          row.txnType
                            ? `${lc.label} · ${TXN_TYPE_LABEL[row.txnType] ?? row.txnType}`
                            : lc.label
                        }
                        className="overflow-hidden whitespace-nowrap px-2.5 py-1.5 text-muted-foreground"
                      >
                        {lc.abbrev}
                      </td>
                      <td className="overflow-hidden whitespace-nowrap px-2.5 py-1.5">
                        {row.project !== null ? (
                          <span
                            title={row.project}
                            className="rounded bg-[hsl(var(--primary))]/15 px-1 py-0.5 text-[9px] font-medium text-[hsl(var(--primary))]"
                          >
                            {proj}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="overflow-hidden whitespace-nowrap px-2.5 py-1.5 tabular-nums text-muted-foreground">
                        {/* A carried-forward row shows its ORIGINAL due day as the primary
                            value — every carried row's effective date is "today" (pure
                            noise) — plus a compact age badge. `overdueAge` is set by
                            `toRow` EXACTLY when the row is carried forward, so this one
                            check drives the whole affordance. */}
                        {row.overdueAge !== null ? (
                          <span
                            role="img"
                            aria-label="Edasi kantud tänasesse"
                            title={`Algne tähtaeg ${formatEtDate(row.overdueAge.since)} — üle tähtaja ${row.overdueAge.days} p; edasi kantud tänasesse (${formatEtDate(row.tahtaeg)})`}
                            className="inline-flex items-center gap-1.5"
                          >
                            <DateText
                              iso={row.overdueAge.since}
                              className="font-medium text-[hsl(var(--cost))]"
                            />
                            <span className="rounded bg-[hsl(var(--cost))]/15 px-1 py-0.5 text-[9px] font-semibold leading-none text-[hsl(var(--cost))]">
                              {row.overdueAge.days} p
                            </span>
                          </span>
                        ) : (
                          <DateText iso={row.tahtaeg} />
                        )}
                      </td>
                      <td className="whitespace-nowrap px-2.5 py-1.5 tabular-nums text-muted-foreground">
                        {row.docDate !== null ? (
                          <DateText iso={row.docDate} />
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                      <MoneyCells row={row} />
                      <td className="px-2.5 py-1.5 text-right">
                        {row.jaak !== 0 ? (
                          <SignedEur cents={row.jaak} />
                        ) : (
                          <span className="tabular-nums text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="px-2.5 py-1.5 text-right">
                        <SettlementSourceCell entry={row.e} />
                      </td>
                      <LedgerStatusCell
                        row={row}
                        // The delayed settlement/document hover annotation now anchors ONLY
                        // on the Staatus cell (not the whole row): hovering the status opens
                        // the SAME GroupHoverCard / DocHoverCard the calendar uses, after the
                        // SAME dwell, while a row click opens the document — a clear split of
                        // "hover = info" vs "click = open" (L14).
                        onMouseEnter={(ev) =>
                          hoverIntent.start({
                            pos: { id: row.e.id, x: ev.clientX, y: ev.clientY },
                            isGroup: row.e.groupId !== null,
                          })
                        }
                        onMouseLeave={clearHover}
                      />
                      <td
                        className={cn(
                          'sticky right-0 border-l border-border/60 bg-card px-2.5 py-1.5 text-right',
                          // Lift the open row's whole sticky cell (and its z-50 popover) above
                          // sibling sticky action cells in later rows; closed rows stay z-10.
                          isOpen ? 'z-40' : 'z-10',
                        )}
                      >
                        {onOpenEntry && (
                          <>
                            {/* A persistent, labelled open affordance (the ▤ icon alone read
                                as "a box to chase"): the word "Ava" sits inline beside it,
                                dim by default and lifting to the primary accent when the row
                                is hovered (group-hover) — so the whole-row open is visible,
                                not discovered by accident. Decorative (aria-hidden); the
                                accessible open control stays the ▤ DirectOpenButton + the
                                row's own role/aria-label (L14 direct-open). */}
                            <span
                              aria-hidden
                              className="mr-1 align-middle text-[10px] font-medium text-muted-foreground/60 transition-colors group-hover:text-[hsl(var(--primary))]"
                            >
                              Ava
                            </span>
                            <DirectOpenButton
                              onOpen={() => onOpenEntry(row.e.id)}
                              label={row.dok}
                              className="mr-1 align-middle text-[11px]"
                            />
                          </>
                        )}
                        <div className="relative inline-block text-left">
                          <button
                            type="button"
                            onClick={(ev) => openActions(row.e.id, ev.currentTarget)}
                            aria-expanded={isOpen}
                            aria-label={`Halda ${row.dok}`}
                            className={cn(
                              'rounded border px-2 py-0.5 text-[10px] transition-colors',
                              isOpen
                                ? 'border-primary/60 bg-primary/15 text-primary'
                                : 'border-border text-foreground hover:bg-accent',
                            )}
                          >
                            Halda
                          </button>
                          {isOpen && (
                            <>
                              <div
                                className="fixed inset-0 z-40"
                                aria-hidden="true"
                                onClick={(ev) => {
                                  // The full-screen backdrop is a DOM descendant of the row,
                                  // so its close-click would bubble to the row and re-open the
                                  // detail — stop it at the popover boundary.
                                  ev.stopPropagation();
                                  setOpenActionId(null);
                                }}
                              />
                              <div
                                role="dialog"
                                aria-label={`Halda — ${row.dok}`}
                                // Clicks inside the Halda menu drive their own controls; keep
                                // them from bubbling to the row's open-detail handler.
                                onClick={(ev) => ev.stopPropagation()}
                                className={cn(
                                  'absolute right-0 z-50 max-h-[60vh] w-64 space-y-2 overflow-y-auto rounded-md border border-border bg-card p-3 text-left shadow-xl',
                                  openUp ? 'bottom-full mb-1' : 'top-full mt-1',
                                )}
                              >
                                <div className="flex items-center justify-between text-[10px]">
                                  <span className="uppercase tracking-wide text-muted-foreground">Looja</span>
                                  <span className="text-foreground">{row.looja ?? '—'}</span>
                                </div>

                                {onPriority && (
                                  <div className="space-y-1">
                                    <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                                      Prioriteet
                                    </span>
                                    <div className="flex flex-wrap gap-1">
                                      {PRIORITIES.map((p) => (
                                        <button
                                          key={p}
                                          type="button"
                                          onClick={() =>
                                            onPriority(row.e.id, row.e.priority === p ? null : p)
                                          }
                                          aria-pressed={row.e.priority === p}
                                          className={cn(
                                            'rounded px-1.5 py-0.5 text-[10px] font-medium transition-colors',
                                            row.e.priority === p
                                              ? PRIORITY_CLS[p]
                                              : 'border border-border text-muted-foreground hover:text-foreground',
                                          )}
                                        >
                                          {PRIORITY_LABEL[p]}
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                <div className="space-y-1">
                                  <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                                    Kommentaar
                                  </span>
                                  {onComment ? (
                                    <input
                                      defaultValue={row.comment ?? ''}
                                      aria-label={`Kommentaar — ${row.dok}`}
                                      onKeyDown={(ev) => {
                                        if (ev.key === 'Enter')
                                          commitComment(
                                            row.e.id,
                                            (ev.target as HTMLInputElement).value,
                                            row.comment,
                                          );
                                        else if (ev.key === 'Escape') setOpenActionId(null);
                                      }}
                                      onBlur={(ev) => commitComment(row.e.id, ev.target.value, row.comment)}
                                      className="w-full rounded border border-primary/50 bg-background px-1.5 py-1 text-[11px] text-foreground outline-none focus:border-primary"
                                    />
                                  ) : (
                                    <p className="text-[11px] text-muted-foreground">{row.comment ?? '—'}</p>
                                  )}
                                </div>

                                {hasActions && row.status !== 'settled' && (
                                  <div className="space-y-1.5 border-t border-border/60 pt-2">
                                    <div className="flex flex-wrap items-center gap-1">
                                      <EtDateInput
                                        value={dateFor(row.e.id)}
                                        onChangeIso={(iso) =>
                                          setActionDate((m) => ({ ...m, [row.e.id]: iso }))
                                        }
                                        aria-label="Vali kuupäev"
                                        title="Vali kuupäev tegevuseks (edasi kandmine / tasutuks märkimine / poolitamine)"
                                        className="px-1 py-0.5 text-[10px]"
                                      />
                                      {onReschedule && (
                                        <button
                                          type="button"
                                          onClick={() => onReschedule(row.e.id, dateFor(row.e.id))}
                                          title="Kanna tähtaeg valitud kuupäeva — jääb saldosse (edasi kantud, idempotentne)"
                                          className="rounded border border-border px-1.5 py-0.5 text-[10px] text-foreground transition-colors hover:bg-accent"
                                        >
                                          Kanna
                                        </button>
                                      )}
                                      {/* A payroll net-wage row settles EMPLOYEE-BY-EMPLOYEE (0037 events
                                          mirror onto the entry) — whole-entry Makstuks / Tükelda / Lahenda
                                          would double-count against those events, so they yield to a hint;
                                          the SAME per-employee surface opens from the row's detail (L14).
                                          Kanna stays: rescheduling moves the date, never the money. */}
                                      {payrollNetEntryMonth(row.e.externalId) !== null ? (
                                        <span className="px-1 text-[10px] text-muted-foreground">
                                          Tasumine töötajate kaupa — ava kirje
                                        </span>
                                      ) : (
                                        <>
                                      {/* Only while a POSITIVE outstanding gap exists (finance-core
                                          `outstandingOf`) — a negative-gross document (KRE) has no
                                          bookable gap, so offering the panel would only 400 server-side;
                                          the detail's Arveldus applies the same gate (L14). */}
                                      {onMarkPaid && outstandingOf(row.e) > 0 && (
                                        <button
                                          type="button"
                                          onClick={() =>
                                            setMarkPaidId((cur) => (cur === row.e.id ? null : row.e.id))
                                          }
                                          aria-expanded={markPaidId === row.e.id}
                                          title="Märgi dokument tasutuks: kuupäev + kogu jääk või osaline summa (kinnitusega)"
                                          className={cn(
                                            'rounded border px-1.5 py-0.5 text-[10px] transition-colors',
                                            markPaidId === row.e.id
                                              ? 'border-[hsl(var(--income))]/60 bg-[hsl(var(--income))]/15 text-[hsl(var(--income))]'
                                              : 'border-[hsl(var(--income))]/50 text-[hsl(var(--income))] hover:bg-[hsl(var(--income))]/10',
                                          )}
                                        >
                                          Makstuks
                                        </button>
                                      )}
                                      {onSplit && (
                                        <button
                                          type="button"
                                          onClick={() => {
                                            setSplitId((cur) => (cur === row.e.id ? null : row.e.id));
                                            setSplitAmt('');
                                          }}
                                          aria-expanded={splitId === row.e.id}
                                          title="Poolita dokument: tõsta osa summast valitud kuupäevale (sama tegevus, mille kalender lohistusel pakub)"
                                          className={cn(
                                            'rounded border px-1.5 py-0.5 text-[10px] transition-colors',
                                            splitId === row.e.id
                                              ? 'border-primary/60 bg-primary/15 text-primary'
                                              : 'border-border text-foreground hover:bg-accent',
                                          )}
                                        >
                                          Tükelda
                                        </button>
                                      )}
                                      {onResolve && (
                                        <button
                                          type="button"
                                          onClick={() => {
                                            setResolveId((cur) => (cur === row.e.id ? null : row.e.id));
                                            setResolveReason('');
                                            setResolveFee('');
                                            setResolveFactor('');
                                            setResolveType('bad_debt_writeoff');
                                          }}
                                          aria-expanded={resolveId === row.e.id}
                                          title="Lahenda jääk: kanna maha (ümardus / lootusetu nõue, vaikne) või faktooring (lisab finantskulu rea) — summa arvutab server"
                                          className={cn(
                                            'rounded border px-1.5 py-0.5 text-[10px] transition-colors',
                                            resolveId === row.e.id
                                              ? 'border-primary/60 bg-primary/15 text-primary'
                                              : 'border-border text-foreground hover:bg-accent',
                                          )}
                                        >
                                          Lahenda
                                        </button>
                                      )}
                                        </>
                                      )}
                                    </div>
                                    {/* The register's mount of the ONE shared MarkPaidPanel (L14):
                                        date + full/partial + confirm; the amount is booked
                                        server-side against the authoritative gap (L9). */}
                                    {onMarkPaid && markPaidId === row.e.id && (
                                      <MarkPaidPanel
                                        outstandingCents={outstandingOf(row.e)}
                                        isIncome={row.e.kind === 'income'}
                                        defaultDate={dateFor(row.e.id)}
                                        pending={false}
                                        compact
                                        onConfirm={(intent) => {
                                          onMarkPaid(row.e.id, intent);
                                          setMarkPaidId(null);
                                        }}
                                        onCancel={() => setMarkPaidId(null)}
                                      />
                                    )}
                                    {onSplit &&
                                      splitId === row.e.id &&
                                      (() => {
                                        const moveCents = parseEurCents(splitAmt);
                                        const canSplit =
                                          moveCents !== null && moveCents > 0 && moveCents < row.e.money.gross;
                                        const preview = canSplit ? splitMoney(row.e.money, moveCents) : null;
                                        return (
                                          <div className="flex flex-col items-start gap-1 rounded border border-primary/40 bg-primary/5 p-1.5">
                                            <label className="flex items-center gap-1 text-[10px] text-muted-foreground">
                                              <span>Tõsta {dateFor(row.e.id)} →</span>
                                              <input
                                                type="text"
                                                inputMode="decimal"
                                                aria-label="Tõstetav summa eurodes"
                                                value={splitAmt}
                                                onChange={(ev) => setSplitAmt(ev.target.value)}
                                                placeholder="nt 500"
                                                className="w-20 rounded border border-border bg-background px-1 py-0.5 text-[10px] text-foreground outline-none focus:border-primary/60"
                                              />
                                              <span>€</span>
                                            </label>
                                            {preview && (
                                              <span
                                                className="text-[10px] text-muted-foreground"
                                                data-testid="ledger-split-preview"
                                              >
                                                Jääb{' '}
                                                <span className="tabular-nums text-foreground/80">
                                                  {formatEur(preview.remainder.gross)}
                                                </span>{' '}
                                                · liigub{' '}
                                                <span className="tabular-nums text-foreground/80">
                                                  {formatEur(preview.moved.gross)}
                                                </span>
                                              </span>
                                            )}
                                            <button
                                              type="button"
                                              disabled={!canSplit}
                                              onClick={() => {
                                                onSplit(row.e.id, dateFor(row.e.id), moveCents!);
                                                setSplitId(null);
                                                setSplitAmt('');
                                              }}
                                              className="rounded border border-primary/50 bg-primary/15 px-1.5 py-0.5 text-[10px] font-medium text-primary transition-colors hover:bg-primary/25 disabled:cursor-not-allowed disabled:opacity-40"
                                            >
                                              Poolita
                                            </button>
                                          </div>
                                        );
                                      })()}
                                    {onResolve &&
                                      resolveId === row.e.id &&
                                      (() => {
                                        const feeCents = parseEurCents(resolveFee);
                                        const reasonOk = resolveReason.trim().length > 0;
                                        const factoring = resolveType === 'factoring_fee';
                                        const canResolve =
                                          reasonOk &&
                                          (!factoring ||
                                            (feeCents !== null &&
                                              feeCents > 0 &&
                                              resolveFactor.trim().length > 0));
                                        return (
                                          <div className="flex flex-col items-stretch gap-1.5 rounded border border-primary/40 bg-primary/5 p-1.5">
                                            <span className="text-[10px] text-muted-foreground">
                                              Sulgeb jäägi{' '}
                                              <span className="tabular-nums text-foreground/80">
                                                {formatEur(row.jaak)}
                                              </span>
                                            </span>
                                            <div className="flex flex-wrap gap-1">
                                              {RESOLVE_TYPES.map((t) => (
                                                <button
                                                  key={t}
                                                  type="button"
                                                  onClick={() => setResolveType(t)}
                                                  aria-pressed={resolveType === t}
                                                  className={cn(
                                                    'rounded border px-1.5 py-0.5 text-[10px] font-medium transition-colors',
                                                    resolveType === t
                                                      ? 'border-primary/60 bg-primary/15 text-primary'
                                                      : 'border-border text-muted-foreground hover:text-foreground',
                                                  )}
                                                >
                                                  {RESOLVE_TYPE_LABEL[t]}
                                                </button>
                                              ))}
                                            </div>
                                            <input
                                              type="text"
                                              value={resolveReason}
                                              onChange={(ev) => setResolveReason(ev.target.value)}
                                              aria-label="Lahenduse põhjus"
                                              placeholder="Põhjus (kohustuslik)"
                                              className="w-full rounded border border-border bg-background px-1.5 py-1 text-[10px] text-foreground outline-none focus:border-primary/60"
                                            />
                                            {factoring && (
                                              <div className="flex flex-wrap items-center gap-1">
                                                <input
                                                  type="text"
                                                  inputMode="decimal"
                                                  value={resolveFee}
                                                  onChange={(ev) => setResolveFee(ev.target.value)}
                                                  aria-label="Faktooringu tasu eurodes"
                                                  placeholder="Tasu €"
                                                  className="w-20 rounded border border-border bg-background px-1 py-0.5 text-[10px] text-foreground outline-none focus:border-primary/60"
                                                />
                                                <input
                                                  type="text"
                                                  value={resolveFactor}
                                                  onChange={(ev) => setResolveFactor(ev.target.value)}
                                                  aria-label="Faktoor (osapool)"
                                                  placeholder="Faktoor"
                                                  className="min-w-0 flex-1 rounded border border-border bg-background px-1 py-0.5 text-[10px] text-foreground outline-none focus:border-primary/60"
                                                />
                                              </div>
                                            )}
                                            <button
                                              type="button"
                                              disabled={!canResolve}
                                              onClick={() => {
                                                const intent: ResolveSettlementIntent =
                                                  resolveType === 'factoring_fee'
                                                    ? {
                                                        type: 'factoring_fee',
                                                        reason: resolveReason.trim(),
                                                        effectiveOn: dateFor(row.e.id),
                                                        fee: { cents: feeCents!, basis: 'net' },
                                                        factor: resolveFactor.trim(),
                                                      }
                                                    : {
                                                        type: resolveType,
                                                        reason: resolveReason.trim(),
                                                        effectiveOn: dateFor(row.e.id),
                                                      };
                                                onResolve(row.e.id, intent);
                                                closeResolve();
                                              }}
                                              className="self-start rounded border border-primary/50 bg-primary/15 px-1.5 py-0.5 text-[10px] font-medium text-primary transition-colors hover:bg-primary/25 disabled:cursor-not-allowed disabled:opacity-40"
                                            >
                                              Lahenda
                                            </button>
                                          </div>
                                        );
                                      })()}
                                  </div>
                                )}

                                {onDelete && row.e.source === 'manual' && (
                                  // Hard-delete a MANUAL entry — the document-level
                                  // destructive action (the server is the authoritative
                                  // manual-only guard). Reachable from every lens via
                                  // drill-to-register (L14). Closes the popover on click.
                                  <div className="border-t border-border/60 pt-2">
                                    <button
                                      type="button"
                                      onClick={() => {
                                        onDelete(row.e.id);
                                        setOpenActionId(null);
                                      }}
                                      className="w-full rounded border border-[hsl(var(--cost))]/50 px-1.5 py-1 text-[10px] font-medium text-[hsl(var(--cost))] transition-colors hover:bg-[hsl(var(--cost))]/10"
                                    >
                                      Kustuta kirje
                                    </button>
                                  </div>
                                )}
                              </div>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
            <tfoot>
              {/* A sticky bottom totals row (req 4 + req 5). It MIRRORS the header sticky
                  scheme: ONLY the select cell pins left and the Halda cell pins right; the
                  "Kokku" label + every money/settlement total live in the scrolling middle
                  (sticky bottom only), so a total is NEVER hidden behind a wide pinned cell
                  on horizontal scroll. colSpan comes from the column model, never a magic
                  number; the "Kokku" money cell(s) are the SIGNED, prepayment-netted
                  cash-flow total (`signedEffectiveSumEntries`) — collapsed to the active
                  mode or expanded to Neto · KM · Bruto — the "Jääk" total is the SIGNED net
                  outstanding (`signedOutstandingSum`; `outstandingOf` already subtracts the
                  received-prepayment offset via `settledAmountOf`, so a fully-funded lõpparve
                  leaves no phantom Jääk and the pair reconciles with the netted "Kokku") and
                  paid comes from `effectiveSettlementRatioSummary`, never a component-side
                  sum (L9). */}
              <tr className="[&>td]:border-t-2 [&>td]:border-border font-semibold">
                <td className="sticky bottom-0 left-0 z-30 bg-card px-2.5 py-2" />
                <td
                  className="sticky bottom-0 z-20 whitespace-nowrap bg-card px-2.5 py-2"
                  colSpan={LEAD_COLUMNS.length}
                >
                  Kokku · {sorted.length} kirjet
                </td>
                {summaExpanded ? (
                  <>
                    <td className={cn('sticky bottom-0 z-20 bg-card px-2.5 py-2 text-right tabular-nums', netCellEmph)}>
                      <SignedEur cents={signedTotal.net} />
                    </td>
                    <td className={cn('sticky bottom-0 z-20 bg-card px-2.5 py-2 text-right tabular-nums', KM_CELL)}>
                      <SignedEur cents={signedTotal.vat} />
                    </td>
                    <td className={cn('sticky bottom-0 z-20 bg-card px-2.5 py-2 text-right tabular-nums', grossCellEmph)}>
                      <SignedEur cents={signedTotal.gross} />
                    </td>
                  </>
                ) : (
                  <td className="sticky bottom-0 z-20 bg-card px-2.5 py-2 text-right tabular-nums">
                    <span className="mr-1 text-[10px] font-normal text-muted-foreground/70">{modeLabel}</span>
                    <SignedEur cents={pickSum(signedTotal, mode)} />
                  </td>
                )}
                <td
                  className="sticky bottom-0 z-20 bg-card px-2.5 py-2 text-right tabular-nums"
                  title="Jääk kokku — veeru „Jääk” summa (alati bruto): laekumata tulud plussis, tasumata kulud miinuses. Neto/Bruto lüliti seda ei muuda."
                >
                  <span className="mr-1 text-[10px] font-normal text-muted-foreground/70">Bruto</span>
                  <SignedEur cents={signedOutstanding} />
                </td>
                {/* The Allikas column carries no footer total (a settlement-source rank
                    has no sum) — an empty cell keeps the totals row aligned with the body. */}
                <td className="sticky bottom-0 z-20 bg-card px-2.5 py-2" />
                <td
                  className="sticky bottom-0 z-20 bg-card px-2.5 py-2 text-right tabular-nums text-[hsl(var(--settled))]"
                  title="Laekunud / tasutud kokku — reaalselt liikunud raha (bruto) nähtavate ridade peale; % näitab, kui suur osa brutosummast on kaetud."
                >
                  <span className="mr-1 text-[10px] font-normal text-muted-foreground/70">Laekunud/tasutud</span>
                  {formatEur(settle.paid)}
                  <span className="ml-1 text-[10px] text-muted-foreground">{formatSettledPct(settle.ratioBps)}</span>
                </td>
                <td className="sticky bottom-0 right-0 z-30 border-l border-border bg-card px-2.5 py-2" />
              </tr>
            </tfoot>
          </table>
        </div>
        {/* The delayed hover annotation is portalled to <body>: the overlay root carries
            backdrop-blur, which re-anchors any position:fixed descendant to itself, so an
            in-tree card would clamp to the dialog, not the viewport. Same Group/Doc cards
            the calendar uses (L14) — grouped → installment card, single → document card;
            the intent payload (not a lookup) already chose which anchor is set. */}
        {groupHover &&
          createPortal(
            <GroupHoverCard pos={groupHover} entries={entries} today={today} />,
            document.body,
          )}
        {docHover &&
          createPortal(
            <DocHoverCard pos={docHover} entries={entries} today={today} mode={mode} />,
            document.body,
          )}
      </div>
    </div>
  );
}
